<template>
  <section :class="['scan-suite', { 'scan-suite--camera-first': !showHeader }]">
    <div v-if="showHeader" class="scan-suite__hero glass-surface workspace-panel">
      <div>
        <span class="workspace-kicker"><i class="fas fa-qrcode"></i> Scan mode</span>
        <h2 class="workspace-title">Escaneo en vivo para ingreso rápido.</h2>
        <p class="workspace-copy">Usa el payload estable del ticket para validar acceso, dejar trazabilidad y detectar al instante si una entrada ya fue usada.</p>
      </div>

      <div class="scan-suite__pills">
        <span><i class="fas fa-ticket-alt"></i> {{ event.name }}</span>
        <span><i :class="paused ? 'fas fa-pause' : 'fas fa-video'"></i> {{ paused ? 'Cámara pausada' : 'Cámara activa' }}</span>
      </div>
    </div>

    <div class="scan-suite__layout">
      <article class="scan-suite__camera-card glass-surface workspace-panel">
        <div v-if="!showHeader" class="scan-suite__camera-head">
          <div>
            <span class="scan-suite__eyebrow"><i class="fas fa-qrcode"></i> QR listo</span>
            <strong>{{ event.name || 'Control de acceso' }}</strong>
          </div>
          <span><i :class="paused ? 'fas fa-pause' : 'fas fa-video'"></i> {{ paused ? 'Pausada' : 'Activa' }}</span>
        </div>

        <div v-if="cameraActive" class="scan-suite__camera-shell">
          <qr-stream :key="cameraKey" :camera="selectedCamera" @init="onInit" @decode="onDecode" class="scan-suite__camera"></qr-stream>
          <div class="scan-suite__overlay">
            <div class="scan-suite__frame">
              <div class="scan-suite__corner scan-suite__corner--tl"></div>
              <div class="scan-suite__corner scan-suite__corner--tr"></div>
              <div class="scan-suite__corner scan-suite__corner--bl"></div>
              <div class="scan-suite__corner scan-suite__corner--br"></div>
              <div class="scan-suite__beam"></div>
            </div>
          </div>
        </div>

        <div v-else-if="loading" class="scan-suite__state">
          <spring-spinner :size="64" color="#9cb8ff" />
          <p>Validando payload y comprobando la entrada...</p>
        </div>

        <div v-else-if="success" class="scan-suite__result scan-suite__result--success">
          <span class="scan-suite__result-kicker">Acceso concedido</span>
          <h3>{{ ticketData.firstName }} {{ ticketData.lastName }}</h3>
          <p>{{ ticketData.productName }}</p>
          <div class="scan-suite__result-grid">
            <span>{{ ticketData.seatName || 'Acceso libre' }}</span>
            <span>{{ ticketData.ticketCode }}</span>
            <span>{{ ticketData.scannedAtLabel }}</span>
          </div>
          <button class="workspace-button workspace-button--primary" type="button" @click="restartSearch">
            <i class="fas fa-qrcode"></i>
            <span>Escanear otra entrada</span>
          </button>
        </div>

        <div v-else-if="err" class="scan-suite__result scan-suite__result--error">
          <span class="scan-suite__result-kicker">Acceso bloqueado</span>
          <h3>{{ errorTitle }}</h3>
          <p>{{ errorMessage }}</p>
          <div v-if="result || lastScanInfo" class="scan-suite__result-grid">
            <span v-if="result" class="scan-suite__payload-chip">QR: {{ result }}</span>
            <span v-if="lastScanInfo">{{ lastScanInfo }}</span>
          </div>
          <button class="workspace-button" type="button" @click="restartSearch">
            <i class="fas fa-rotate"></i>
            <span>Reintentar</span>
          </button>
        </div>

        <div v-else class="scan-suite__state scan-suite__state--paused">
          <p>{{ paused ? 'Cámara en pausa. Reanuda cuando quieras volver a escanear.' : 'Reiniciando cámara...' }}</p>
        </div>
      </article>

      <aside class="scan-suite__side-card glass-surface workspace-panel">
        <div class="scan-suite__side-block">
          <span class="workspace-kicker"><i class="fas fa-code"></i> Último payload</span>
          <code>{{ result || 'Sin lectura todavia' }}</code>
        </div>

        <div class="scan-suite__side-block">
          <span class="workspace-kicker"><i class="fas fa-video"></i> Cámara</span>
          <div class="scan-suite__camera-options">
            <button
              v-for="option in cameraOptions"
              :key="option.value"
              class="scan-suite__camera-option"
              :class="{ 'scan-suite__camera-option--active': selectedCamera === option.value }"
              type="button"
              :disabled="loading"
              @click="selectCamera(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
          <p>{{ cameraHelpText }}</p>
        </div>

        <div class="workspace-actions workspace-actions--compact scan-suite__controls">
          <button v-if="!paused && !loading" class="workspace-button" type="button" @click="pauseScan">
            <i class="fas fa-pause"></i>
            <span>Pausar</span>
          </button>
          <button v-if="paused" class="workspace-button" type="button" @click="resumeScan">
            <i class="fas fa-play"></i>
            <span>Reanudar</span>
          </button>
          <button class="workspace-button workspace-button--primary" type="button" @click="restartSearch">
            <i class="fas fa-rotate"></i>
            <span>Reiniciar</span>
          </button>
        </div>

        <div class="scan-suite__side-block">
          <span class="workspace-kicker"><i class="fas fa-fingerprint"></i> Trazabilidad</span>
          <p>Cuando un acceso se procesa, guardamos quién escaneó y la hora exacta para auditoría operativa.</p>
        </div>
      </aside>
    </div>
  </section>
</template>

<script>
import { QrStream } from 'vue3-qr-reader'
import { SpringSpinner } from 'epic-spinners'
import { apiService } from '../../common/api.services.js'
import { formatEventDate } from '@/common/event-timezone.js'

const SCAN_DATE_FORMAT = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZoneName: 'short',
}

const CAMERA_RESTART_DELAY_MS = 450
const SCANNER_CAMERA_STORAGE_KEY = 'fomo_scanner_camera'

export default {
  components: { QrStream, SpringSpinner },

  props: {
    event: {
      required: true,
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      result: '',
      errorTitle: 'No pudimos validar esta entrada',
      errorMessage: '',
      lastScanInfo: '',
      loading: false,
      success: false,
      err: false,
      paused: false,
      ticketData: {
        firstName: '',
        lastName: '',
        seatName: '',
        productName: '',
        ticketCode: '',
        scannedAtLabel: '',
      },
      isDecoding: false,
      cameraKey: 0,
      cameraReady: true,
      cameraRestartTimer: null,
      selectedCamera: 'auto',
      cameraOptions: [
        { value: 'front', label: 'Frontal' },
        { value: 'rear', label: 'Trasera' },
        { value: 'auto', label: 'Auto' },
      ],
    }
  },

  computed: {
    cameraActive() {
      return this.cameraReady && !this.success && !this.loading && !this.err && !this.paused
    },

    cameraHelpText() {
      if (this.selectedCamera === 'front') {
        return 'Usa la camara frontal. En desktop evita que Auto tome camaras virtuales como OBS.'
      }
      if (this.selectedCamera === 'rear') {
        return 'Usa la camara trasera, ideal para escanear desde celular.'
      }
      return 'Auto deja que el navegador elija la camara disponible.'
    },
  },

  mounted() {
    this.configureDefaultCamera()
  },

  beforeUnmount() {
    this.clearCameraRestartTimer()
    this.releaseCameraTracks()
  },

  methods: {
    async configureDefaultCamera() {
      const storedCamera = window.localStorage.getItem(SCANNER_CAMERA_STORAGE_KEY)
      if (this.cameraOptions.some((option) => option.value === storedCamera)) {
        this.selectedCamera = storedCamera
        return
      }

      if (this.isMobileDevice()) {
        this.selectedCamera = 'rear'
        return
      }

      try {
        const devices = await navigator.mediaDevices?.enumerateDevices?.()
        const videoInputs = Array.isArray(devices) ? devices.filter((device) => device.kind === 'videoinput') : []
        if (videoInputs.length > 1) {
          this.selectedCamera = 'front'
        }
      } catch (error) {
        this.selectedCamera = 'auto'
      }
    },

    isMobileDevice() {
      return window.matchMedia('(max-width: 768px)').matches || /Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent)
    },

    selectCamera(camera) {
      if (this.selectedCamera === camera || this.loading) {
        return
      }
      this.selectedCamera = camera
      window.localStorage.setItem(SCANNER_CAMERA_STORAGE_KEY, camera)
      this.restartSearch()
    },

    pauseScan() {
      this.paused = true
      this.stopCameraStream()
    },

    resumeScan() {
      this.paused = false
      this.remountCamera()
    },

    clearCameraRestartTimer() {
      if (this.cameraRestartTimer) {
        window.clearTimeout(this.cameraRestartTimer)
        this.cameraRestartTimer = null
      }
    },

    remountCamera() {
      this.clearCameraRestartTimer()
      this.releaseCameraTracks()
      this.cameraReady = false
      this.cameraKey += 1
      this.$nextTick(() => {
        this.releaseCameraTracks()
        this.cameraRestartTimer = window.setTimeout(() => {
          this.cameraRestartTimer = null
          if (!this.success && !this.loading && !this.err && !this.paused) {
            this.cameraReady = true
          }
        }, CAMERA_RESTART_DELAY_MS)
      })
    },

    stopCameraStream() {
      this.releaseCameraTracks()
      this.cameraReady = false
    },

    releaseCameraTracks() {
      if (!this.$el || !this.$el.querySelectorAll) {
        return
      }
      const videos = this.$el.querySelectorAll('video')
      videos.forEach((video) => {
        const stream = video.srcObject
        if (stream && stream.getTracks) {
          stream.getTracks().forEach((track) => track.stop())
        }
        video.srcObject = null
      })
    },

    restartSearch() {
      this.result = ''
      this.errorTitle = 'No pudimos validar esta entrada'
      this.errorMessage = ''
      this.lastScanInfo = ''
      this.loading = false
      this.success = false
      this.err = false
      this.paused = false
      this.isDecoding = false
      this.ticketData = {
        firstName: '',
        lastName: '',
        seatName: '',
        productName: '',
        ticketCode: '',
        scannedAtLabel: '',
      }
      this.remountCamera()
    },

    async onDecode(payload) {
      if (this.isDecoding) {
        return
      }

      this.isDecoding = true
      this.result = payload
      this.loading = true
      this.success = false
      this.err = false
      this.errorMessage = ''
      this.lastScanInfo = ''
      this.stopCameraStream()

      try {
        const data = await apiService(`/api/scan-invoice-item/${this.event.id}/`, 'POST', { payload })

        if (data && data.message === 'Entrada Procesada' && data.ticket) {
          this.success = true
          this.ticketData = {
            firstName: data.ticket.first_name,
            lastName: data.ticket.last_name,
            seatName: data.ticket.seat_name,
            productName: data.ticket.product_name,
            ticketCode: data.ticket.access_code_snapshot || 'Sin codigo',
            scannedAtLabel: this.formatScanDate(data.ticket.scanned_at),
          }
          return
        }

        this.handleApiError(data)
      } catch (error) {
        this.handleError('No pudimos procesar la validacion de la entrada.')
      } finally {
        this.loading = false
        this.isDecoding = false
      }
    },

    handleApiError(data) {
      if (data && data.err) {
        this.errorTitle = data.status === 'already_used' || data.err === 'Entrada en uso' || data.err === 'Entrada ya utilizada' ? 'Esta entrada ya fue utilizada' : 'Acceso no permitido'
        this.errorMessage = data.message || data.err
        const lastScan = data.last_scan || {}
        const scannedBy = lastScan.scanned_by_name || data.scanned_by_name
        const scannedAt = lastScan.scanned_at || data.scanned_at
        if (scannedBy || scannedAt || data.allowed_usage) {
          const usageInfo = data.allowed_usage ? ` · Uso ${data.usage || 0} de ${data.allowed_usage}` : ''
          this.lastScanInfo = `Ultimo registro: ${scannedBy || 'usuario no identificado'} · ${this.formatScanDate(scannedAt)}${usageInfo}`
        }
        this.stopCameraStream()
        this.err = true
        return
      }

      this.handleError('No se encontro una entrada valida para este QR.')
    },

    async onInit(promise) {
      const activeCameraKey = this.cameraKey
      try {
        await promise
      } catch (error) {
        if (activeCameraKey !== this.cameraKey || !this.cameraReady) {
          return
        }
        if (error.name === 'OverconstrainedError' && this.selectedCamera !== 'auto') {
          this.selectedCamera = 'auto'
          this.remountCamera()
          return
        }
        this.handleError(this.getCameraError(error.name))
      }
    },

    handleError(message) {
      this.errorMessage = message
      this.stopCameraStream()
      this.err = true
      this.loading = false
    },

    formatScanDate(value) {
      if (!value) {
        return 'Sin hora registrada'
      }

      return formatEventDate(value, this.event, SCAN_DATE_FORMAT)
    },

    getCameraError(errorName) {
      const errors = {
        NotAllowedError: 'Debes otorgar permiso para acceder a la camara.',
        NotFoundError: 'No se encontro una camara en este dispositivo.',
        NotSupportedError: 'Se requiere HTTPS o localhost para usar la camara.',
        NotReadableError: 'La camara ya esta en uso por otra aplicacion.',
        OverconstrainedError: 'Las camaras instaladas no cumplen con el requisito solicitado.',
        StreamApiNotSupportedError: 'Este navegador no soporta la API de camara.',
        InsecureContextError: 'El acceso a la camara solo funciona en contexto seguro.',
      }

      return errors[errorName] || `Error de camara: ${errorName}`
    },
  },
}
</script>

<style scoped>
.scan-suite {
  display: grid;
  gap: 18px;
}

.scan-suite--camera-first {
  gap: 12px;
}

.scan-suite__hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 18px;
  background:
    radial-gradient(circle at top left, rgba(214, 221, 232, 0.09), transparent 28%),
    radial-gradient(circle at top right, rgba(98, 76, 144, 0.16), transparent 24%),
    linear-gradient(180deg, rgba(11, 14, 23, 0.96), rgba(8, 8, 14, 0.94)) !important;
}

.scan-suite__hero .workspace-title {
  max-width: 880px;
  font-family: 'Syne', 'Sora', sans-serif;
  line-height: 1;
  letter-spacing: -0.04em;
}

.scan-suite__layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 380px);
  gap: 18px;
  align-items: start;
}

.scan-suite__result-kicker {
  color: #cfd8ea;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.scan-suite__result h3 {
  margin: 8px 0 6px;
  color: #fff;
  font-family: 'Syne', 'Sora', sans-serif;
  font-size: clamp(1.55rem, 3vw, 2.4rem);
  line-height: 1;
  letter-spacing: -0.04em;
}

.scan-suite__result p,
.scan-suite__side-block p,
.scan-suite__result-grid span,
.scan-suite__side-block code,
.scan-suite__state p {
  color: rgba(194, 204, 223, 0.82);
}

.scan-suite__pills,
.scan-suite__result-grid,
.scan-suite__controls {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.scan-suite__pills span,
.scan-suite__result-grid span,
.scan-suite__side-block code {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(194, 212, 224, 0.1);
  font-family: 'Space Grotesk', sans-serif;
}

.scan-suite__payload-chip {
  max-width: min(100%, 560px);
  overflow-wrap: anywhere;
}

.scan-suite__camera-card {
  padding: 18px !important;
}

.scan-suite--camera-first .scan-suite__layout {
  grid-template-columns: minmax(0, 1fr) minmax(270px, 330px);
  gap: 14px;
}

.scan-suite--camera-first .scan-suite__camera-card {
  padding: 10px !important;
  border-radius: 28px !important;
}

.scan-suite__camera-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px 12px;
}

.scan-suite__camera-head div {
  min-width: 0;
  display: grid;
  gap: 3px;
}

.scan-suite__camera-head strong {
  color: var(--text-main);
  overflow: hidden;
  font-family: 'Syne', 'Sora', sans-serif;
  font-size: clamp(1.2rem, 2.1vw, 1.75rem);
  line-height: 1;
  letter-spacing: -0.04em;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scan-suite__camera-head span,
.scan-suite__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  width: fit-content;
  color: var(--text-muted);
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.scan-suite__camera-head > span {
  flex-shrink: 0;
  min-height: 30px;
  padding: 0 10px;
  border-radius: 999px;
  color: rgba(8, 17, 20, 0.92);
  background: linear-gradient(135deg, rgba(243, 248, 251, 0.94), rgba(138, 223, 216, 0.9));
}

.scan-suite__camera-shell {
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  min-height: clamp(360px, 54vh, 620px);
  background:
    radial-gradient(circle at center, rgba(92, 128, 230, 0.26), transparent 38%),
    linear-gradient(135deg, rgba(24, 37, 86, 0.82), rgba(9, 11, 20, 0.96));
}

.scan-suite--camera-first .scan-suite__camera-shell {
  min-height: clamp(430px, 66vh, 760px);
  border-radius: 22px;
}

.scan-suite__camera {
  width: 100%;
  height: 100%;
  min-height: clamp(360px, 54vh, 620px);
  display: block;
}

.scan-suite--camera-first .scan-suite__camera {
  min-height: clamp(430px, 66vh, 760px);
}

.scan-suite__camera-shell :deep(video),
.scan-suite__camera-shell :deep(canvas) {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover;
  border-radius: inherit;
}

.scan-suite__overlay {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(6, 8, 13, 0.36);
  pointer-events: none;
}

.scan-suite__frame {
  position: relative;
  width: min(52vw, 320px);
  height: min(52vw, 320px);
  border-radius: 28px;
  border: 1px solid rgba(188, 223, 255, 0.18);
  box-shadow:
    0 0 0 1px rgba(125, 164, 255, 0.08),
    0 28px 52px rgba(0, 0, 0, 0.26);
}

.scan-suite__beam {
  position: absolute;
  inset: 18px 14px auto;
  height: 3px;
  border-radius: 999px;
  background: linear-gradient(90deg, transparent, rgba(157, 210, 255, 0.9), transparent);
  box-shadow: 0 0 18px rgba(157, 210, 255, 0.7);
  animation: scan-beam 2.4s ease-in-out infinite;
}

.scan-suite__corner {
  position: absolute;
  width: 26px;
  height: 26px;
  border-color: #d8ebff;
  border-style: solid;
}

.scan-suite__corner--tl { top: 14px; left: 14px; border-width: 3px 0 0 3px; border-top-left-radius: 14px; }
.scan-suite__corner--tr { top: 14px; right: 14px; border-width: 3px 3px 0 0; border-top-right-radius: 14px; }
.scan-suite__corner--bl { bottom: 14px; left: 14px; border-width: 0 0 3px 3px; border-bottom-left-radius: 14px; }
.scan-suite__corner--br { bottom: 14px; right: 14px; border-width: 0 3px 3px 0; border-bottom-right-radius: 14px; }

.scan-suite__state,
.scan-suite__result {
  min-height: clamp(360px, 54vh, 620px);
  display: grid;
  align-content: center;
  justify-items: center;
  gap: 12px;
  text-align: center;
}

.scan-suite__result--success {
  background: linear-gradient(180deg, rgba(83, 129, 111, 0.22), rgba(10, 12, 18, 0));
}

.scan-suite__result--error {
  background: linear-gradient(180deg, rgba(153, 82, 82, 0.18), rgba(10, 12, 18, 0));
}

.scan-suite__side-card,
.scan-suite__side-block {
  display: grid;
  gap: 12px;
}

.scan-suite__side-card {
  position: sticky;
  top: 18px;
  align-content: start;
}

.scan-suite--camera-first .scan-suite__side-card {
  gap: 10px;
  padding: 14px !important;
}

.scan-suite--camera-first .scan-suite__side-block code {
  min-height: 72px;
  max-height: 150px;
  padding: 14px;
  border-radius: 18px;
}

.scan-suite__side-block code {
  display: block;
  min-height: 92px;
  max-height: 220px;
  padding: 18px;
  border-radius: 24px;
  white-space: pre-wrap;
  word-break: break-word;
}

.scan-suite__camera-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.scan-suite__camera-option {
  flex: 1 1 88px;
  min-height: 38px;
  border: 1px solid rgba(194, 212, 224, 0.12);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.045);
  color: rgba(235, 242, 255, 0.86);
  font-family: 'Space Grotesk', sans-serif;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
}

.scan-suite__camera-option:hover:not(:disabled),
.scan-suite__camera-option--active {
  border-color: rgba(156, 184, 255, 0.62);
  background: rgba(96, 123, 220, 0.22);
  color: #ffffff;
}

.scan-suite__camera-option:disabled {
  cursor: wait;
  opacity: 0.58;
}

.scan-suite__controls .workspace-button {
  flex: 1 1 140px;
}

@keyframes scan-beam {
  0%, 100% { transform: translateY(0); opacity: 0.6; }
  50% { transform: translateY(260px); opacity: 1; }
}

@media (max-width: 1100px) {
  .scan-suite__hero {
    grid-template-columns: 1fr;
  }

  .scan-suite__layout {
    grid-template-columns: 1fr;
  }

  .scan-suite--camera-first .scan-suite__layout {
    grid-template-columns: 1fr;
  }

  .scan-suite__side-card {
    position: static;
  }
}

@media (max-width: 640px) {
  .scan-suite__camera-shell,
  .scan-suite__camera,
  .scan-suite__state,
  .scan-suite__result {
    min-height: 360px;
  }

  .scan-suite--camera-first .scan-suite__camera-shell,
  .scan-suite--camera-first .scan-suite__camera {
    min-height: min(68vh, 520px);
  }

  .scan-suite__camera-head {
    align-items: flex-start;
    flex-direction: column;
    padding: 6px 7px 10px;
  }

  .scan-suite__camera-head strong {
    white-space: normal;
  }

  .scan-suite__frame {
    width: min(74vw, 270px);
    height: min(74vw, 270px);
  }

  .scan-suite__controls .workspace-button {
    width: 100%;
  }
}
</style>
